import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Typography,
} from '@mui/material';
import typeMacroProcessApi, {
  TypeMacroProcess,
} from '../../../../../api/typeMacroProcess';
import { useAuth } from '../../../../../Auth/Auth';

type Props = {
  isEditing?: boolean;
  selectedRow?: TypeMacroProcess;
  setSelectedRow?: React.Dispatch<
    React.SetStateAction<TypeMacroProcess | undefined>
  >;
  fetchTypeMacroProcesses?: () => Promise<void>;
};

const DEFAULT_USER_ID = 1;

export const Form: React.FC<Props> = ({
  isEditing = false,
  selectedRow,
  setSelectedRow,
  fetchTypeMacroProcesses,
}) => {
  const auth = useAuth();
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [ok, setOk] = useState<boolean>(false);
  const [typeMacroProcess, setTypeMacroProcess] = useState<TypeMacroProcess>({
    name: '',
    comments: '',
    isActive: 'SI',
    creationId: auth?.user?.id || DEFAULT_USER_ID,
  });

  useEffect(() => {
    if (isEditing && setSelectedRow) {
      setSelectedRow(undefined);
      if (selectedRow) {
        setTypeMacroProcess({
          id: selectedRow.id,
          name: selectedRow.name,
          comments: selectedRow.comments,
          isActive: selectedRow.isActive,
          creationId: auth?.user?.id || DEFAULT_USER_ID,
        });
      }
    }
  }, [auth?.user?.id, isEditing, selectedRow, setSelectedRow]);

  const handleOnClickSave = useCallback(async () => {
    setError('');
    const typeMacroProcessId = typeMacroProcess.id;
    setIsLoading(true);
    try {
      if (typeMacroProcessId) {
        await typeMacroProcessApi.updateTypeMacroProcess(
          typeMacroProcessId,
          typeMacroProcess
        );
      } else {
        await typeMacroProcessApi.createTypeMacroProcess(typeMacroProcess);
      }
      setOk(true);
    } catch {
      setError('Ha ocurrido un error al guardar el tipo de macro proceso.');
    }
    setTypeMacroProcess({
      name: '',
      comments: '',
      isActive: 'SI',
      creationId: auth?.user?.id || DEFAULT_USER_ID,
    });
    if (isEditing && setSelectedRow) {
      setSelectedRow(undefined);
    }
    setIsLoading(false);
    if (fetchTypeMacroProcesses) {
      await fetchTypeMacroProcesses();
    }
  }, [
    auth?.user?.id,
    fetchTypeMacroProcesses,
    isEditing,
    typeMacroProcess,
    setSelectedRow,
  ]);

  return (
    <>
      <Box
        component='form'
        sx={{
          '& .MuiTextField-root': { m: 1, width: '40ch' },
        }}
        noValidate
        autoComplete='off'
      >
        <TextField
          required
          label='Nombre'
          helperText='Nombre del tipo de Macro Proceso'
          value={typeMacroProcess.name}
          onChange={(e) =>
            setTypeMacroProcess((prevState) => ({
              ...prevState,
              name: e.target.value,
            }))
          }
          error={!!!typeMacroProcess.name}
          inputProps={{ maxLength: 200 }}
        />
        <TextField
          label='Observaciones'
          helperText='Observaciones del tipo de Macro Proceso'
          value={typeMacroProcess.comments ?? ''}
          onChange={(e) =>
            setTypeMacroProcess((prevState) => ({
              ...prevState,
              comments: e.target.value,
            }))
          }
        />
        {isEditing && (
          <TextField
            select
            required
            label='Activo'
            value={typeMacroProcess.isActive}
            onChange={(e) =>
              setTypeMacroProcess((prevState) => ({
                ...prevState,
                isActive: e.target.value,
              }))
            }
          >
            {[
              { value: 'SI', label: 'SI' },
              { value: 'NO', label: 'NO' },
            ].map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Box>
      <Button
        variant='contained'
        sx={{ textTransform: 'none' }}
        onClick={handleOnClickSave}
        disabled={
          isLoading ||
          !!!typeMacroProcess.name ||
          !!!typeMacroProcess.isActive ||
          (isEditing && !!!typeMacroProcess.id)
        }
      >
        {isLoading ? <CircularProgress size={25} /> : 'Guardar'}
      </Button>
      {error && <Typography color='red'>{error}</Typography>}
      {ok && (
        <Typography marginTop={'10px'}>
          El tipo de Macro Proceso se ha guardado correctamente.
        </Typography>
      )}
    </>
  );
};
